import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { Icon } from "./Icon";
import "./Select.css";

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
  sub?: string;
};

interface SelectProps<T extends string = string> {
  value: T | null;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  variant?: "field" | "chip";
  /** chip variant: the prefix label ("Tipo"). */
  label?: string;
  disabled?: boolean;
  /** chip filters: show a "Todos" option that clears the selection. */
  clearable?: boolean;
  /** Label for the clear option (defaults to "Todos"). */
  clearLabel?: string;
  id?: string;
  ariaLabel?: string;
}

type Coords = {
  left: number;
  width: number;
  top: number | null;
  bottom: number | null;
};

const PANEL_MAX = 320;

export function Select<T extends string = string>({
  value,
  onChange,
  options,
  placeholder = "Selecione…",
  variant = "field",
  label,
  disabled = false,
  clearable = false,
  clearLabel = "Todos",
  id,
  ariaLabel,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [coords, setCoords] = useState<Coords | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  // Flattened, navigable rows: the clear row (if any) gets index 0.
  const rowCount = options.length + (clearable ? 1 : 0);

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const flipUp = spaceBelow < PANEL_MAX && spaceAbove > spaceBelow;
    setCoords({
      left: r.left,
      width: r.width,
      top: flipUp ? null : r.bottom + 6,
      bottom: flipUp ? window.innerHeight - r.top + 6 : null,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    // Position the active row on the current selection.
    const selIdx = options.findIndex((o) => o.value === value);
    setActiveIdx(selIdx >= 0 ? selIdx + (clearable ? 1 : 0) : 0);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition, options, value, clearable]);

  // Outside-click (trigger + portaled panel both count as "inside") and Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const item = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    if (!item) return;
    const list = listRef.current;
    const itemTop = item.offsetTop;
    const itemBot = itemTop + item.offsetHeight;
    if (itemTop < list.scrollTop) list.scrollTop = itemTop;
    else if (itemBot > list.scrollTop + list.clientHeight)
      list.scrollTop = itemBot - list.clientHeight;
  }, [activeIdx, open]);

  function pick(idx: number) {
    if (clearable && idx === 0) {
      onChange("" as T);
    } else {
      const opt = options[idx - (clearable ? 1 : 0)];
      if (opt) onChange(opt.value);
    }
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, rowCount - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(activeIdx);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  const panelStyle: CSSProperties = coords
    ? {
        left: coords.left,
        width: coords.width,
        top: coords.top != null ? coords.top : "auto",
        bottom: coords.bottom != null ? coords.bottom : "auto",
        maxHeight: PANEL_MAX,
      }
    : { visibility: "hidden" };

  const isChip = variant === "chip";

  const panel = (
    <div className="ui-select-panel" style={panelStyle} role="listbox" ref={panelRef}>
      <div className="ui-select-panel-scroll" ref={listRef}>
        {clearable && (
          <button
            type="button"
            data-idx={0}
            className={`ui-select-opt ${value == null || value === "" ? "on" : ""} ${
              activeIdx === 0 ? "active" : ""
            }`}
            role="option"
            aria-selected={value == null || value === ""}
            onClick={() => pick(0)}
            onMouseEnter={() => setActiveIdx(0)}
          >
            <span className="ui-select-opt-l">
              <span className="ui-select-opt-label">{clearLabel}</span>
            </span>
            {(value == null || value === "") && (
              <Icon name="check" size={14} className="ui-select-opt-check" />
            )}
          </button>
        )}
        {options.map((o, i) => {
          const idx = i + (clearable ? 1 : 0);
          const on = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              data-idx={idx}
              className={`ui-select-opt ${on ? "on" : ""} ${idx === activeIdx ? "active" : ""}`}
              role="option"
              aria-selected={on}
              onClick={() => pick(idx)}
              onMouseEnter={() => setActiveIdx(idx)}
            >
              <span className="ui-select-opt-l">
                <span className="ui-select-opt-label">{o.label}</span>
                {o.sub && <span className="ui-select-opt-sub">{o.sub}</span>}
              </span>
              {on && <Icon name="check" size={14} className="ui-select-opt-check" />}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={`ui-select ui-select-${variant}`}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={
          isChip
            ? `ui-select-chip ${selected ? "has-value" : ""} ${open ? "open" : ""}`
            : `ui-select-trigger ${open ? "open" : ""}`
        }
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        {isChip ? (
          <>
            {label && <span className="ui-select-chip-label">{label}</span>}
            {selected ? (
              <>
                {label && <span className="ui-select-chip-sep">:</span>}
                <span className="ui-select-chip-val">{selected.label}</span>
              </>
            ) : (
              <span className="ui-select-chip-val muted">{placeholder}</span>
            )}
            <Icon name="chevron-down" size={13} className="ui-select-caret" />
          </>
        ) : (
          <>
            {selected ? (
              <span className="ui-select-value">
                <span className="ui-select-value-l">{selected.label}</span>
                {selected.sub && <span className="ui-select-value-s">{selected.sub}</span>}
              </span>
            ) : (
              <span className="ui-select-placeholder">{placeholder}</span>
            )}
            <Icon name="chevron-down" size={14} className="ui-select-caret" />
          </>
        )}
      </button>

      {open && createPortal(panel, document.body)}
    </div>
  );
}
